/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input} from '@angular/core';
import template from './external-ad-unit-edit-usage.component.html';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {AppSharedModule} from '../common/app-shared.module';
import {AdminResourcePagesModule} from '../common/controller/admin-resource-pages.module';
import {TargetingModule} from '../targeting/targeting.module';

interface UsageListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

interface UsageListView {
  items: any[];
  hasItems: boolean;
  pageSize: number;
  totalCount: number;
  currentPage: number;
}

@Component({
  standalone: true,
  imports: [AppSharedModule, AdminResourcePagesModule, TargetingModule],
  selector: 'adn-external-ad-unit-edit-usage',
  template
})
export class ExternalAdUnitEditUsageComponent {
  @Input() isNew = false;
  @Input() adUnits: any = {};
  @Input() creatives: any = {};

  showAdUnitDetail = false;
  showCreativeDetail = false;

  get adUnitView(): UsageListView { return applyUsageListView(this.adUnits); }
  get creativeView(): UsageListView { return applyUsageListView(this.creatives); }

  readonly adUnitColumns: UsageListColumn[] = [
    {param: 'name'},
    {param: '', label: 'dimensions', sortable: false},
    {param: 'description'},
    {param: '', label: 'site', sortable: false},
    {param: '', label: 'tagId', sortable: false}
  ];

  readonly creativeColumns: UsageListColumn[] = [
    {param: 'name'},
    {param: 'Layout', label: 'layout'},
    {param: 'width', label: 'dimensions'},
    {param: '', label: 'targeting', sortable: false}
  ];

  readonly perms: any;

  constructor(
    @Inject(LocalUserPermissions) LocalUserPermissions: any
  ) {
    this.perms = LocalUserPermissions.getAllVisPermissions();
  }

  toggleAdUnitDetail(): void {
    this.showAdUnitDetail = !this.showAdUnitDetail;
  }

  toggleCreativeDetail(): void {
    this.showCreativeDetail = !this.showCreativeDetail;
  }

  isColumnSortable(column: UsageListColumn): boolean {
    return column.sortable !== false && !!column.param;
  }

  columnLabel(column: UsageListColumn): string {
    if (column.labelInstant) {
      return column.labelInstant;
    }
    const key = column.label || column.param;
    return key ? 'label.' + key : '';
  }

  sortBy(list: any, column: UsageListColumn): void {
    if (!this.isColumnSortable(column) || !list?.makeSortQuery) {
      return;
    }
    if (column.numeric) {
      list.makeSortQuery(column.param, column.numeric);
    } else {
      list.makeSortQuery(column.param);
    }
  }

  changePage(list: any, page: number): void {
    if (!list || page < 1 || page > this.totalPages(list) || page === list.currentPage) {
      return;
    }
    list.currentPage = page;
    list.changePage();
  }

  totalPages(list: any): number {
    return Math.max(1, Math.ceil((list?.listState?.totalCount() || 0) / (list?.pageSize || 1)));
  }

  pageNumbers(list: any): number[] {
    return visiblePageNumbers(list?.listState?.currentPage() || 1, this.totalPages(list));
  }

  pageRangeStart(list: any): number {
    return list.pageSize * (list.currentPage - 1) + 1;
  }

  pageRangeEnd(list: any): number {
    return Math.min(list.pageSize * list.currentPage, list.totalCount);
  }

}

function applyUsageListView(list: any): UsageListView {
  const items = list?.listState?.items() || [];
  return {
    items,
    hasItems: items.length > 0,
    pageSize: list?.listState?.pageSize || 25,
    totalCount: list?.listState?.totalCount() || 0,
    currentPage: list?.listState?.currentPage() || 1
  };
}
